import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { BookCover } from "../components/BookCover";
import { StarDisplay } from "../components/StarRating";
import { Search, BookMarked, Users, Heart, Tag, Globe, BookOpen, Star, Compass, ArrowRight } from "lucide-react";
import { getStoredLang } from "../components/LanguagePicker";
import { t, useLang } from "../lib/i18n";
import { ARTICLES } from "./BlogPage";

const QUOTES = [];

export default function HomePage() {
  useLang();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthed = user && typeof user === "object";
  const isPlus = isAuthed && user.is_plus;

  const [stats, setStats] = useState({ books: 0, reviews: 0, readers: 0 });
  const [topBooks, setTopBooks] = useState([]);
  const [search, setSearch] = useState("");

  const quote = useMemo(() => ({ q: t("home.quote.q"), a: t("home.quote.a"), w: t("home.quote.w") }), []);
  const [livePreview, setLivePreview] = useState([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    api.get("/stats/overview").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/books", { params: { sort: "top" } }).then(({ data }) => setTopBooks(data.slice(0, 6))).catch(() => {});
  }, []);

  // Live search results dropdown — same as Discover.
  useEffect(() => {
    if (!search.trim() || search.trim().length < 2) {
      setLivePreview([]); setPreviewOpen(false); return;
    }
    const ctrl = new AbortController();
    setPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const lang = getStoredLang();
        const params = new URLSearchParams({
          q: search.trim(), language: lang, limit: "20",
          fields: "key,title,author_name,first_publish_year,cover_i,language",
        });
        const r = await fetch(`https://openlibrary.org/search.json?${params}`, { signal: ctrl.signal });
        const data = await r.json();
        // Trust the server-side `language=` filter; OL search often omits
        // the `language` field per hit even when the record matches.
        const filtered = (data.docs || []).slice(0, 6);
        setLivePreview(filtered);
        setPreviewOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setLivePreview([]);
      } finally {
        setPreviewLoading(false);
      }
    }, 280);
    return () => { clearTimeout(timer); ctrl.abort(); };
  }, [search]);

  const onSearch = (e) => {
    e.preventDefault();
    if (!search.trim()) return;
    navigate(`/discover?q=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div className="fade-up" data-testid="home-page">
      {/* HERO with centered search bar */}
      <section className="relative">
        <div className="max-w-4xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-12 text-center">
          <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-[#4A4030] border border-[rgba(10,10,10,0.16)] bg-[#FAF5E6] px-3 py-1.5 rounded-sm mb-6">
            <span className="w-1.5 h-1.5 bg-[#0A0A0A] rounded-full" />
            {t("home.smart_recs")}
          </div>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.03em] text-[#0A0A0A]">
            {t("home.hero_title")}
          </h1>
          <p className="text-base md:text-lg text-[#4A4030] leading-relaxed mt-6 max-w-2xl mx-auto">
            {t("home.hero_subtitle")}
          </p>

          <form onSubmit={onSearch} className="mt-10 max-w-2xl mx-auto relative" data-testid="home-search-form">
            <div className="search-pill flex items-center pl-5 pr-2 py-1">
              <Search className="w-5 h-5 text-[#4A4030] flex-shrink-0" strokeWidth={1.5} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onFocus={() => livePreview.length > 0 && setPreviewOpen(true)}
                onBlur={() => setTimeout(() => setPreviewOpen(false), 150)}
                placeholder={t("home.search_placeholder")}
                className="flex-1 bg-transparent border-0 px-4 py-3 text-base focus:outline-none placeholder:text-[#8C7F65]"
                data-testid="home-search-input"
              />
              <button type="submit" className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-[11px] px-5 py-2.5 rounded-full transition-colors" data-testid="home-search-button">
                {t("common.search")}
              </button>
            </div>
            {previewOpen && (livePreview.length > 0 || previewLoading) && (
              <div className="absolute z-30 left-0 right-0 mt-2 bg-[#FAF5E6] border border-[rgba(10,10,10,0.12)] shadow-[0_20px_60px_rgba(10,10,10,0.18)] text-left max-h-96 overflow-y-auto" data-testid="home-search-preview">
                {previewLoading && livePreview.length === 0 && (
                  <div className="px-5 py-4 font-mono uppercase tracking-widest text-[10px] text-[#A3A3A3]">{t("home.searching")}</div>
                )}
                {livePreview.map((doc) => (
                  <button
                    type="button"
                    key={doc.key}
                    onMouseDown={(e) => { e.preventDefault(); navigate(`/discover?q=${encodeURIComponent(doc.title || search.trim())}`); }}
                    className="w-full flex items-center gap-4 px-5 py-3 hover:bg-[#F2EAD3] border-b last:border-0 border-[rgba(10,10,10,0.06)] text-left"
                    data-testid={`home-preview-${(doc.key || "").replace(/\W/g, "_")}`}
                  >
                    <div className="w-9 h-12 flex-shrink-0 bg-[#E8DFC1] border border-[rgba(10,10,10,0.08)] overflow-hidden">
                      {doc.cover_i ? (
                        <img src={`https://covers.openlibrary.org/b/id/${doc.cover_i}-S.jpg`} alt="" className="w-full h-full object-cover" loading="lazy" />
                      ) : null}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[#0A0A0A] truncate">{doc.title}</div>
                      <div className="font-mono uppercase tracking-widest text-[9px] text-[#525252] truncate">
                        {(doc.author_name && doc.author_name[0]) || t("home.unknown_author")}{doc.first_publish_year ? ` · ${doc.first_publish_year}` : ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65] mt-3">
              {t("home.powered_by")}
            </div>
          </form>

          {!isAuthed && (
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/register" data-testid="hero-register-btn">
                <button className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-6 py-3.5 rounded-sm transition-colors inline-flex items-center gap-2">
                  <Compass className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("home.get_my_picks")}
                </button>
              </Link>
              <Link to="/subscribe" data-testid="hero-plus-btn" className={isPlus ? "hidden" : ""}>
                <button className="border border-[rgba(10,10,10,0.18)] text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-6 py-3.5 rounded-sm hover:bg-black/5 transition-colors">
                  {t("home.unlock_plus")}
                </button>
              </Link>
            </div>
          )}
          {isAuthed && (
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/recommendations" data-testid="hero-recs-btn">
                <button className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-6 py-3.5 rounded-sm transition-colors inline-flex items-center gap-2">
                  <Compass className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("home.see_picks")}
                </button>
              </Link>
            </div>
          )}
        </div>

        {/* Founder's mission — sits above the rotating literary quotes */}
        <div className="border-y border-[rgba(10,10,10,0.12)] bg-[#0A0A0A] py-10 md:py-14" data-testid="founder-mission-band">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-[#F2EAD3]/60 mb-4">{t("footer.mission_label")}</div>
            <blockquote className="font-heading text-2xl md:text-4xl text-[#F2EAD3] italic leading-snug tracking-tight" data-testid="founder-quote">
              {t("footer.mission_quote")}
            </blockquote>
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#F2EAD3]/70 mt-5" data-testid="founder-attrib">
              {t("footer.founder")}
            </div>
          </div>
        </div>

        {/* Rotating quote band */}
        <div className="border-y border-[rgba(10,10,10,0.10)] bg-[#EBE3CB] py-8 md:py-10" data-testid="home-quote-band">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <div className="font-heading text-xl md:text-2xl text-[#0A0A0A] italic leading-snug" data-testid="home-quote">
              &ldquo;{quote.q}&rdquo;
            </div>
            <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mt-3" data-testid="home-quote-author">
              — {quote.a} · {quote.w}
            </div>
          </div>
        </div>

        {/* Live stats strip */}
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12">
          <div className="grid grid-cols-3 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)]">
            <StatCell label={t("home.books_logged")} value={stats.books} testId="stat-books" />
            <StatCell label={t("home.reviews_written")} value={stats.reviews} testId="stat-reviews" />
            <StatCell label={t("home.active_readers")} value={stats.readers} testId="stat-readers" />
          </div>
        </div>
      </section>

      {/* Recommendations showcase — concrete examples of what the engine produces */}
      <section className="border-t border-[rgba(10,10,10,0.10)] bg-[#0A0A0A] text-[#F2EAD3]" data-testid="recs-showcase">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-16 md:py-24">
          <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#F2EAD3]/60 mb-3">{t("home.showcase_eyebrow")}</div>
          <h2 className="font-heading text-3xl md:text-5xl tracking-tight max-w-3xl">{t("home.showcase_title")}</h2>
          <p className="text-[#F2EAD3]/70 mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
            {t("home.showcase_para")}
          </p>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <PreviewPick because={t("home.pick1_because")} title="Real Life" author="Brandon Taylor" reason={t("home.pick1_reason")} />
            <PreviewPick because={t("home.pick2_because")} title="The Left Hand of Darkness" author="Ursula K. Le Guin" reason={t("home.pick2_reason")} />
            <PreviewPick because={t("home.pick3_because")} title="The Underground Railroad" author="Colson Whitehead" reason={t("home.pick3_reason")} />
          </div>
          <div className="mt-12">
            <Link to={isAuthed ? "/recommendations" : "/register"} className="inline-flex items-center gap-2 bg-[#F2EAD3] hover:bg-[#FAF5E6] text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-6 py-3.5 rounded-sm transition-colors" data-testid="recs-showcase-cta">
              <Compass className="w-3.5 h-3.5" strokeWidth={1.5} /> {isAuthed ? t("home.generate_picks") : t("home.start_mine")}
            </Link>
          </div>
        </div>
      </section>

      {/* How Reads works — walkthrough for anonymous visitors only. Logged-
          in users already know how the app works. */}
      {!isAuthed && (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="how-it-works">
        <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] mb-3">{t("home.how_picks_work")}</div>
        <h2 className="font-heading text-3xl md:text-5xl text-[#0A0A0A] tracking-tight max-w-2xl">{t("home.three_steps")}</h2>
        <p className="text-[#4A4030] mt-5 max-w-2xl text-base md:text-lg leading-relaxed">
          {t("home.three_steps_para")}
        </p>
        <div className="grid md:grid-cols-3 gap-10 md:gap-14 mt-14">
          <Step
            n="01"
            icon={<BookOpen className="w-6 h-6" strokeWidth={1.5} />}
            title={t("home.step1_title")}
            body={t("home.step1_body")}
          />
          <Step
            n="02"
            icon={<Star className="w-6 h-6" strokeWidth={1.5} />}
            title={t("home.step2_title")}
            body={t("home.step2_body")}
          />
          <Step
            n="03"
            icon={<Compass className="w-6 h-6" strokeWidth={1.5} />}
            title={t("home.step3_title")}
            body={t("home.step3_body")}
          />
        </div>
        <div className="mt-12">
          <Link to={isAuthed ? "/recommendations" : "/register"} data-testid="how-it-works-cta" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-6 py-3.5 rounded-sm transition-colors">
            <Compass className="w-3.5 h-3.5" strokeWidth={1.5} /> {isAuthed ? t("home.open_recs") : t("home.start_recs")}
          </Link>
        </div>
      </section>
      )}

      {/* Why we built Reads — editorial for anonymous visitors only.
          Members are already sold on the mission. */}
      {!isAuthed && (
      <section className="border-y border-[rgba(10,10,10,0.10)] bg-[#EBE3CB]" data-testid="why-reads">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28">
          <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] mb-4">{t("home.why_reads")}</div>
          <h2 className="font-heading text-3xl md:text-5xl text-[#0A0A0A] tracking-tight">{t("home.log_not_feed")}</h2>
          <div className="mt-10 space-y-6 text-[#0A0A0A] text-base md:text-[17px] leading-[1.75]">
            <p>{t("home.why_para1")}</p>
            <p>{t("home.why_para2")}</p>
            <p>{t("home.why_para3")}</p>
            <p className="font-heading text-xl md:text-2xl text-[#0A0A0A] italic leading-snug pt-4">
              {t("home.mission_line")}
            </p>
          </div>
        </div>
      </section>
      )}

      {/* What Reads does — feature grid for anonymous visitors only. */}
      {!isAuthed && (
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
        <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] mb-3">{t("home.what_you_get")}</div>
        <h2 className="font-heading text-3xl md:text-5xl text-[#0A0A0A] tracking-tight max-w-2xl">{t("home.everything_needs")}</h2>
        <div className="grid md:grid-cols-3 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)] mt-10">
          <Feature icon={<BookMarked className="w-5 h-5" strokeWidth={1.5} />} title={t("home.feat_library_title")} body={t("home.feat_library_body")} />
          <Feature icon={<Compass className="w-5 h-5" strokeWidth={1.5} />} title={t("home.feat_picks_title")} body={t("home.feat_picks_body")} />
          <Feature icon={<Globe className="w-5 h-5" strokeWidth={1.5} />} title={t("home.feat_ol_title")} body={t("home.feat_ol_body")} />
          <Feature icon={<Users className="w-5 h-5" strokeWidth={1.5} />} title={t("home.feat_authors_title")} body={t("home.feat_authors_body")} />
          <Feature icon={<Heart className="w-5 h-5" strokeWidth={1.5} />} title={t("home.feat_wishlist_title")} body={t("home.feat_wishlist_body")} />
          <Feature icon={<Tag className="w-5 h-5" strokeWidth={1.5} />} title={t("home.feat_sections_title")} body={t("home.feat_sections_body")} />
        </div>
      </section>
      )}

      {/* Reads+ teaser — free users only */}
      {!isPlus && (
      <section className="border-y border-[rgba(10,10,10,0.10)] bg-[#0A0A0A]" data-testid="reads-plus-teaser">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-20 md:py-24 grid md:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-[10px] text-[#F2EAD3]/60 mb-3 inline-flex items-center gap-2">
              <BookOpen className="w-3 h-3" strokeWidth={1.5} /> Reads+
            </div>
            <h2 className="font-heading text-3xl md:text-5xl text-[#F2EAD3] tracking-tight leading-[1.05]">{t("home.plus_readers_dont")}</h2>
            <p className="text-[#F2EAD3]/80 mt-5 max-w-xl leading-relaxed">
              {t("home.plus_para")}
            </p>
            <div className="mt-8 inline-flex items-end gap-4">
              <div>
                <div className="font-heading text-5xl text-[#F2EAD3] leading-none">€3.99</div>
                <div className="font-mono uppercase tracking-widest text-[10px] text-[#F2EAD3]/70 mt-2">{t("home.per_month")}</div>
              </div>
              <div className="text-[#F2EAD3]/40">{t("home.or")}</div>
              <div>
                <div className="font-heading text-5xl text-[#F2EAD3] leading-none">€35.99</div>
                <div className="font-mono uppercase tracking-widest text-[10px] text-[#F2EAD3]/70 mt-2">{t("home.per_year")}</div>
              </div>
            </div>
          </div>
          <Link to="/subscribe" data-testid="home-plus-cta">
            <button className="bg-[#F2EAD3] hover:bg-white text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-7 py-4 rounded-sm transition-colors inline-flex items-center gap-2 whitespace-nowrap">
              {t("home.see_whats_inside")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </Link>
        </div>
      </section>
      )}

      {/* Reads+ member banner — replaces the teaser for paying subscribers.
          Feels premium instead of promotional. */}
      {isPlus && (
        <section className="border-y border-[rgba(10,10,10,0.10)] bg-gradient-to-r from-[#0A0A0A] via-[#1a1a1a] to-[#0A0A0A]" data-testid="reads-plus-member-banner">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-14 md:py-16 grid md:grid-cols-[1fr_auto] gap-8 items-center">
            <div>
              <div className="font-mono uppercase tracking-[0.3em] text-[10px] text-[#D4B876] mb-3 inline-flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#D4B876]" /> {t("home.plus_member")}
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-[#F2EAD3] tracking-tight leading-[1.05]">
                {t("home.welcome_back")}, {user.name?.split(" ")[0] || "friend"}.
              </h2>
              <p className="text-[#F2EAD3]/70 mt-3 max-w-xl leading-relaxed text-[15px]">
                {t("home.no_caps")}
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <Link to="/recommendations">
                <button className="w-full bg-[#D4B876] hover:bg-[#E4C888] text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-6 py-3.5 rounded-sm transition-colors inline-flex items-center justify-center gap-2 whitespace-nowrap">
                  {t("home.open_my_picks")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </Link>
              <Link to="/library">
                <button className="w-full border border-[#F2EAD3]/25 text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-6 py-3 rounded-sm hover:bg-[#F2EAD3]/5 transition-colors whitespace-nowrap">
                  {t("home.my_library")}
                </button>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Highest rated */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-16 md:pt-24">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] mb-3">{t("home.curated")}</div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0A0A0A] tracking-tight">{t("home.highest_rated")}</h2>
          </div>
          <Link to="/discover" className="font-mono uppercase tracking-widest text-xs text-[#4A4030] hover:text-[#0A0A0A] hidden sm:block">{t("home.see_all")}</Link>
        </div>
        {topBooks.length === 0 ? (
          <div className="border border-dashed border-[rgba(10,10,10,0.16)] p-10 text-center">
            <div className="font-heading text-xl text-[#4A4030]">{t("home.no_books_be_first")}</div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 md:gap-8">
            {topBooks.map((b) => (
              <Link key={b.id} to={`/books/${b.id}`} className="group" data-testid={`top-book-${b.id}`}>
                <BookCover src={b.cover_url} title={b.title} author={b.author} />
                <div className="mt-3 space-y-1">
                  <div className="font-heading text-base text-[#0A0A0A] leading-tight line-clamp-2 group-hover:text-[#4A4030] transition-colors">{b.title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-[#4A4030]">{b.author}</div>
                  <div className="flex items-center gap-2 pt-1">
                    <StarDisplay value={b.avg_rating} size={12} />
                    <span className="font-mono text-[10px] text-[#4A4030]">{b.avg_rating.toFixed(1)} · {b.review_count}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Reading tips / blog teaser — surfaces long-form content everyone
          can benefit from (also gives AdSense the crawlable substance it
          looks for on the homepage). */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24" data-testid="blog-teaser">
        <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
          <div>
            <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] mb-3">Reading tips</div>
            <h2 className="font-heading text-3xl md:text-4xl text-[#0A0A0A] tracking-tight">From the journal</h2>
          </div>
          <Link to="/blog" className="font-mono uppercase tracking-widest text-xs text-[#4A4030] hover:text-[#0A0A0A]" data-testid="home-blog-see-all">
            See all articles →
          </Link>
        </div>
        <div className="grid md:grid-cols-3 gap-px bg-[rgba(10,10,10,0.10)] border border-[rgba(10,10,10,0.10)]">
          {ARTICLES.slice(0, 3).map((a) => (
            <Link
              key={a.slug}
              to={`/blog/${a.slug}`}
              className="bg-[#FAF5E6] hover:bg-[#F2EAD3] p-8 transition-colors group"
              data-testid={`home-blog-${a.slug}`}
            >
              <div className="font-mono uppercase tracking-widest text-[10px] text-[#8B7100] mb-3">
                {a.tag} · {a.minutes} min
              </div>
              <h3 className="font-heading text-xl text-[#0A0A0A] leading-tight tracking-tight group-hover:text-[#4A4030] transition-colors">{a.title}</h3>
              <p className="text-[#4A4030] text-sm mt-3 leading-relaxed line-clamp-3">{a.excerpt}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-[rgba(10,10,10,0.10)]" data-testid="home-final-cta">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <div className="font-mono uppercase tracking-[0.25em] text-xs text-[#4A4030] mb-4">{t("home.start_shelf_today")}</div>
          <h2 className="font-heading text-4xl md:text-6xl text-[#0A0A0A] tracking-tight leading-[1.0]">{t("home.every_book")}</h2>
          <p className="text-[#4A4030] mt-6 max-w-xl mx-auto leading-relaxed">
            {t("home.final_cta_body")}
          </p>
          {!isAuthed ? (
            <div className="mt-10 flex flex-wrap gap-3 justify-center">
              <Link to="/register" data-testid="final-cta-register">
                <button className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-7 py-4 rounded-sm transition-colors inline-flex items-center gap-2">
                  {t("home.create_shelf")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </Link>
              <Link to="/discover" data-testid="final-cta-discover">
                <button className="border border-[rgba(10,10,10,0.18)] text-[#0A0A0A] font-mono uppercase tracking-widest text-xs px-7 py-4 rounded-sm hover:bg-black/5 transition-colors">
                  {t("home.browse_catalogue")}
                </button>
              </Link>
            </div>
          ) : (
            <Link to="/library" data-testid="final-cta-library" className="mt-10 inline-block">
              <button className="bg-[#0A0A0A] hover:bg-[#1F1F1F] text-[#F2EAD3] font-mono uppercase tracking-widest text-xs px-7 py-4 rounded-sm transition-colors inline-flex items-center gap-2">
                {t("home.open_my_library")} <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
              </button>
            </Link>
          )}
        </div>
      </section>
    </div>
  );
}

function Step({ n, icon, title, body }) {
  return (
    <div className="relative">
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#8C7F65] mb-3">{n}</div>
      <div className="text-[#0A0A0A] mb-4">{icon}</div>
      <h3 className="font-heading text-2xl text-[#0A0A0A] mb-3 tracking-tight">{title}</h3>
      <p className="text-[#4A4030] leading-relaxed">{body}</p>
    </div>
  );
}

function PreviewPick({ because, title, author, reason }) {
  return (
    <div className="border border-[#F2EAD3]/15 bg-[#1F1F1F] p-6 flex flex-col gap-3" data-testid="preview-pick">
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#F2EAD3]/55">{because}</div>
      <div>
        <div className="font-heading text-2xl text-[#F2EAD3] tracking-tight">{title}</div>
        <div className="font-mono uppercase tracking-widest text-[10px] text-[#F2EAD3]/60 mt-1">{t("home.by")} {author}</div>
      </div>
      <p className="text-[#F2EAD3]/75 text-sm leading-relaxed">{reason}</p>
    </div>
  );
}

function StatCell({ label, value, testId }) {
  return (
    <div className="bg-[#FAF5E6] p-6 text-center">
      <div className="font-heading text-4xl md:text-5xl text-[#0A0A0A] tabular-nums" data-testid={testId}>{value}</div>
      <div className="font-mono uppercase tracking-widest text-[10px] text-[#4A4030] mt-2">{label}</div>
    </div>
  );
}

function Feature({ icon, title, body }) {
  return (
    <div className="bg-[#FAF5E6] p-8 md:p-10">
      <div className="text-[#0A0A0A] mb-4">{icon}</div>
      <h3 className="font-heading text-xl md:text-2xl text-[#0A0A0A] mb-2 tracking-tight">{title}</h3>
      <p className="text-[#4A4030] text-sm leading-relaxed">{body}</p>
    </div>
  );
}
